import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { ControlledInput, IParams, ISaveIncomeType, Page, Section, Widget } from '../../../shared';
import { useIncomeType, useSaveIncomeType } from '../../hooks/income-type.hook';

export const IncomeTypeEditPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>() as IParams;

    const { data: incomeType, isFetching } = useIncomeType(id);
    const { mutateAsync: saveIncomeType, isPending } = useSaveIncomeType();

    const form = useForm<ISaveIncomeType>({
        resolver: yupResolver(object().shape({ name: string().required() })),
        mode: 'onSubmit',
    });

    useEffect(() => {
        if (incomeType) {
            form.reset(incomeType);
        }
    }, [form, incomeType]);

    const onSubmit = useCallback(
        async (data: ISaveIncomeType) => {
            await saveIncomeType({ data });
            navigate('/admin/income-types');
        },
        [saveIncomeType, navigate],
    );

    const actions = useMemo(
        () => [
            <Button onClick={() => navigate('/admin/income-types')} color="secondary">
                {t('cancel')}
            </Button>,
            <Button variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
                {t('save')}
            </Button>,
        ],
        [form, onSubmit, isPending, t, navigate],
    );

    return (
        <Page
            title={<Box width="300px">{id ? t('editIncomeType') : t('newIncomeType')}</Box>}
            actions={actions}
            onBack={() => navigate(-1)}
            loading={isFetching || isPending}
        >
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Widget footer={[...actions].reverse()}>
                        <Section title={t('data')}>
                            <ControlledInput name="name" label={t('nameLabel')} required sx={{ width: '400px' }} />
                        </Section>
                    </Widget>
                </form>
            </FormProvider>
        </Page>
    );
};
